import type { SNFieldValue, SNTicketLocation } from '@nsc/servicenow/types.js';

/**
 * Reading coordinates out of a ServiceNow location, with its placeholders filtered out.
 *
 * Shared by the self-ticket path (`ticket-queries.ts`) and the colleague view
 * (`_colleague-mapping.ts`) so the placeholder rule can't drift between the two maps.
 */

/** Parse a string-encoded SN coordinate (`{ value: "48.85" }`) to a number, or undefined if blank/NaN. */
export function parseCoord(field: SNFieldValue | undefined): number | undefined {
  if (!field?.value) return undefined;
  const n = Number(field.value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Half-degree box around (0, 0) that ServiceNow's placeholder coordinates fall into.
 *
 * SN does not null out coordinates it doesn't have — it returns a degenerate pair instead, and it
 * uses more than one: tickets in Paris, Trappes and Toulouse came back as `0,0`, while WOT0019850
 * ("19 Rue René Jacques, Issy-Les-Moulineaux") came back as `1,1` (nsctest, 2026-09-17). Both land
 * in the Gulf of Guinea, which puts the pin in the wrong hemisphere *and* stretches a map's
 * `fitBounds` across half the planet, collapsing the real cluster to a dot.
 *
 * Rejecting the box rather than the two exact pairs also covers any further placeholder SN starts
 * emitting. It is safe because the box is open ocean ~600 km off Ghana — no NSC site will ever be
 * there — and it deliberately tests both axes together: longitude 0 on its own is perfectly real
 * (it runs through Greenwich and western France) and must keep working.
 */
const PLACEHOLDER_COORD_LIMIT = 1.5;

/**
 * Read a location's coordinate pair, treating ServiceNow's placeholders as "no coordinates" so the
 * caller falls back to geocoding the address — which, for every case seen so far, resolves
 * correctly.
 */
export function parseLocationCoords(loc: SNTicketLocation | undefined): { lat?: number; lng?: number } {
  const lat = parseCoord(loc?.latitude);
  const lng = parseCoord(loc?.longitude);
  if (
    lat !== undefined &&
    lng !== undefined &&
    Math.abs(lat) <= PLACEHOLDER_COORD_LIMIT &&
    Math.abs(lng) <= PLACEHOLDER_COORD_LIMIT
  ) {
    return {};
  }
  return { lat, lng };
}
